import { logError, logWarn } from '@ptek/client-logger';
import { WHISPER_CONFIG } from '../config/whisper';
import { useRecordingStore } from '../stores';
import {
  AudioChunk,
  TranscriptLine,
  TranscriptMessage,
  TranscriptSnapshotMessage,
} from '../types/recorder';
import { ContinuousCutSource } from './capture/continuousCutSource';
import { createCutterForMimeType } from './capture/cutterRegistry';
import { RotatingOverlapSource } from './capture/rotatingOverlapSource';
import {
  CaptureSource,
  CaptureSourceCallbacks,
  EmittedChunk,
} from './capture/types';
import { SessionRecordingConflictError, sessionsClient } from './sessions';
import {
  BACKOFF_BASE_MS,
  BACKOFF_MAX_MS,
  MAX_UNACKED_BUFFER_BYTES,
  QueuedChunk,
  UNAUTHORIZED_MAX_ATTEMPTS,
  UploadQueue,
  isRetryableUploadError,
} from './uploadQueue';
import { WhisperClient } from './whisperClient';

const CHUNK_DURATION_MS = WHISPER_CONFIG.chunkDurationMs;

// Cap consecutive failed re-arm attempts before treating the failure as fatal (NEB-99247).
const MAX_CONSECUTIVE_ARM_FAILURES = 3;

interface RecordingOptions {
  onRecordingStarted?: () => void;
  onRecordingStopped?: () => void;
  onRecordingError?: (error: Error) => void;
  /**
   * Non-stopping error surface for a FAILED live device swap (NEB-91363). Kept
   * separate from onRecordingError because a failed swap must surface the error
   * WITHOUT stopping the still-active recording on the prior device.
   */
  onSwitchDeviceError?: (error: Error) => void;
  onTimerUpdate?: (elapsedMs: number) => void;
  onSetLiveTranscript?: (
    transcriptLines: Array<{
      timestamp: number;
      text: string;
      confidence: number;
      chunkId: string;
      segmentId?: string;
    }>
  ) => void;
  /** Replace the live transcript when the server sends a durable snapshot. */
  onReplaceLiveTranscript?: (transcriptLines: TranscriptLine[]) => void;
  /**
   * Continuous-capture (flag-on) only: the session setup the legacy path ran
   * BEFORE capture (create durable session, begin-take). It runs concurrently
   * with capture so the microphone opens with zero network round trips;
   * chunks emitted before it resolves wait in the pending buffer.
   */
  prepareSession?: () => Promise<void>;
  /** Continuous-capture only: fired when the whisper upload session resolves. */
  onUploadSessionReady?: (whisperSessionId: string) => void;
  /**
   * Continuous-capture only: fired when the upload session could NOT be
   * established (the recording is ended). Separate from onRecordingError so
   * the UI can distinguish a connection failure from a recording failure.
   */
  onUploadSessionError?: (error: Error) => void;
  /**
   * Continuous-capture only: upload-connectivity transitions during an active
   * recording — 'degraded' on the first retryable chunk-upload failure,
   * 'recovered' once an upload succeeds again. Deliberately NOT
   * onRecordingError: an outage must never stop the recording or surface a
   * fatal UI state (AC3) — failed chunks retry from the queue.
   */
  onUploadConnectionChange?: (status: 'degraded' | 'recovered') => void;
  /** Continuous-capture only: a byte-bound eviction lost audio. */
  onUploadDataLoss?: (details: {
    chunkNumber: number;
    bytes: number;
    boundBytes: number;
  }) => void;
  /** Continuous-capture only: the server rejected the pause checkpoint. */
  onPauseCheckpointError?: (error: Error) => void;
  /**
   * Continuous-capture only: the server rejected a chunk upload with a
   * non-retryable status (e.g. 400/404) — distinct from onUploadDataLoss,
   * which reports a chunk evicted because the upload buffer reached its bound.
   */
  onUploadFailed?: (details: {
    chunkNumber: number;
    bytes: number;
    status?: number;
  }) => void;
  /**
   * The server's silence verdict for one uploaded chunk: true when it skipped
   * the chunk as silent. Both upload paths fire it, and only when the upload's
   * 200 body carried a verdict.
   */
  onChunkSilent?: (chunkNumber: number, silent: boolean) => void;
}

/**
 * Recording-complete signal deferred at stop time because the final slice had
 * not settled (NEB-100347). Values are captured at stop — takeGeneration /
 * takeStartChunkNumber may be reset before the slice's terminal point fires.
 */
interface PendingRecordingCompleteSignal {
  recordingSessionId: string;
  /**
   * The in-flight final slice's recorder; its terminal points flush this.
   * Matched by identity — the slice has no chunk number until (unless) it
   * emits a valid blob.
   */
  sliceRecorder: MediaRecorder;
  takeStart: number;
  takeGeneration: number;
}

/**
 * Chunk-number allocator shared by every take of ONE transport session.
 * clearSession replaces the OBJECT (the next session numbers from 0) while a
 * detached run keeps numbering its late emissions against the old session's
 * counter, so a previous session's tail can never consume the new session's
 * numbers.
 */
interface ChunkCounter {
  next: number;
}

/**
 * Per-recording state of the continuous-capture (flag-on) path. Created by
 * startRecordingContinuous; the flag-off reset paths never touch it.
 * state.continuous is non-null exactly while a continuous-capture recording
 * is active, which is also what routes the public stop/pause/resume/
 * switchDevice entry points — but the run object outlives that: its queue
 * entries reference it as their per-chunk stamp, so a chunk draining after
 * the next take (or session) starts still uploads under THIS run's identity,
 * never live take state.
 */
interface ContinuousCaptureState {
  source: CaptureSource;
  /** The negotiated MediaRecorder mime type — reused to build the fallback source on degrade. */
  mimeType: string;
  options: RecordingOptions;
  counter: ChunkCounter;
  /** The transport session's single ordered uploader (shared across re-record takes). */
  queue: UploadQueue<ContinuousCaptureState>;
  /** This take's first chunk number, captured at start. */
  takeStartChunkNumber: number;
  /** Lowest chunk number this run will emit; queued chunks shift with it. */
  firstChunkNumber: number;
  sessionState: 'pending' | 'ready' | 'failed';
  /** Upload stamps, settled once at session-ready: null = legacy upload path. */
  takeGeneration: number | null;
  whisperSessionId: string | null;
  recordingSessionId: string | null;
  /** Durable session lease captured when this take began. */
  recordingLeaseId: string | null;
  highestEnqueuedChunkNumber: number | null;
  /**
   * Highest chunk number of THIS run confirmed uploaded — the
   * recording-complete fallback signal at the run's terminal point.
   */
  highestUploadedChunkNumber: number | null;
  /** Chunks enqueued but not yet settled (uploaded / failed / dropped). */
  outstandingChunks: number;
  stopped: boolean;
  /**
   * One-way latch: set once a failed ContinuousCutSource has been swapped for a
   * RotatingOverlapSource. A second failure (rotating source, or any failure
   * after this point) takes the fatal path instead of degrading again.
   */
  degradedToRotating: boolean;
  /** Chunk number the stop path marked ?final=true; null until stop. */
  finalChunkNumber: number | null;
  /** True once an upload attempt carrying ?final=true succeeded. */
  finalDelivered: boolean;
  terminalHandled: boolean;
  controlAction: 'pause' | 'stop';
  pauseAcknowledged: boolean;
  /**
   * The establishUploadSession promise for this run. Resolves once the session
   * settles (ready OR failed — establishUploadSession never rejects). The pause
   * path awaits it so a pause fired while the session is still `pending` defers
   * its checkpoint + lease release until the run has an identity to send them
   * with, instead of falsely acknowledging against a no-op control signal.
   */
  sessionEstablished: Promise<void> | null;
}

interface RecorderState {
  isRecording: boolean;
  isPaused: boolean;
  onSetLiveTranscript?: (
    transcriptLines: Array<{
      timestamp: number;
      text: string;
      confidence: number;
      chunkId: string;
      segmentId?: string;
    }>
  ) => void;
  onReplaceLiveTranscript?: (transcriptLines: TranscriptLine[]) => void;
  sessionId: string | null;
  recordingLeaseId: string | null;
  startTime: number;
  /**
   * The current transport session's chunk-number allocator. Chunk numbering
   * stays monotonic across re-records of the same session; clearSession
   * replaces the object so a detached run's late emissions cannot touch the
   * new session's numbering (see ChunkCounter).
   */
  chunkCounter: ChunkCounter;
  /**
   * First chunk number of the CURRENT take (NEB-100035). Chunk numbering stays
   * monotonic across re-records of the same durable session, so the server
   * needs this to know where the latest take begins. Captured in
   * resetTakeState() (fresh session = 0; re-record = the preserved next number).
   */
  takeStartChunkNumber: number;
  takeGeneration: number | null;
  /** Server-authoritative next chunk number for the current take. */
  serverChunkCursor: number | null;
  /** One-shot guard so the legacy-fallback warning is not logged per chunk. */
  warnedMissingTakeGeneration: boolean;
  recordedChunks: Record<string, AudioChunk>;
  mediaRecorder: MediaRecorder | null;
  recordingStream: MediaStream | null;
  captureResources: AudioCaptureResources | null;
  timerInterval: number | null;
  currentChunkTimeout: number | null;
  /**
   * Transcript lines keyed by chunkId. A re-delivered or re-uploaded chunk's
   * lines REPLACE that chunk's previous lines — appending would duplicate
   * transcript text fed to document generation (e.g. the pause-then-stop
   * ?final=true re-upload delivers the same chunk's transcript twice).
   */
  transcriptLinesByChunk: Map<string, TranscriptLine[]>;
  snapshotWatermark: {
    takeGeneration: number;
    maxIncludedChunk: number;
  } | null;
  transcriptChunksReceived: Set<string>;
  expectedChunkIds: Set<string>;
  finalChunkMarked: boolean;
  transcriptCompletionResolve: ((lines: TranscriptLine[]) => void) | null;
  /**
   * Whether the most recent waitForTranscriptCompletion resolution was via the
   * completion gate (true) or the 5s timeout fallback (false). Lets callers
   * surface "transcript incomplete" instead of silently using partial text.
   */
  lastCompletionComplete: boolean;
  // Keep at most one chunk blob in memory (the latest) to allow finalization
  lastChunkIdWithBlob: string | null;
  highestChunkNumberWithBlob: number | null;
  pendingRecordingCompleteSignal: PendingRecordingCompleteSignal | null;
  /**
   * Non-continuous final slices settle asynchronously after MediaRecorder.stop
   * returns. Consumers that make a stop-time decision (for example, whether
   * to start server docgen) wait on these promises so a valid deferred tail is
   * not mistaken for a no-audio recording.
   */
  pendingFinalSliceSettlements: Set<Promise<void>>;
  pendingFinalSliceResolvers: WeakMap<MediaRecorder, () => void>;
  pendingFinalSlicePromises: WeakMap<MediaRecorder, Promise<void>>;
  latestPendingFinalSliceSettlement: Promise<void> | null;
  /**
   * Slices whose onstop handling has not reached a terminal point (blob upload
   * settled, dropped for no-data/too-small, or failed). `MediaRecorder.stop()`
   * flips `.state` to 'inactive' SYNCHRONOUSLY while the onstop that assigns
   * the chunk number and uploads is still queued, so `.state` alone cannot
   * tell stopRecording whether the final slice is still in flight. WeakSet:
   * identity-keyed, and a recorder whose onstop never fires must not pin
   * memory.
   */
  unsettledSliceRecorders: WeakSet<MediaRecorder>;
  recordingOptions: RecordingOptions | null;
  activeDeviceId: string | null;
  isSwitchingDevice: boolean;
  consecutiveArmFailures: number;
  continuous: ContinuousCaptureState | null;
  /**
   * The most recently STARTED continuous run — unlike state.continuous it is
   * not nulled at stop, so a run's terminal point (which can fire after stop
   * resolved) knows whether it still owns the global transcript-completion
   * state or a newer run has taken over.
   */
  latestContinuousRun: ContinuousCaptureState | null;
  /** The current transport session's upload queue (continuous capture only). */
  uploadQueue: UploadQueue<ContinuousCaptureState> | null;
  /**
   * The previous continuous run's source-stop completion: resolves once its
   * tail emission has been NUMBERED. The next start awaits it before
   * capturing the new take's start chunk number, so a late tail can never
   * consume the new take's start (re-scope race (a)).
   */
  lastContinuousTailSettled: Promise<void> | null;
  pausePending: boolean;
}

interface AudioCaptureResources {
  microphoneStream: MediaStream;
  displayStream?: MediaStream;
  mixedStream: MediaStream;
  audioContext?: AudioContext;
  /** Detaches the shared-tab `ended` watcher installed for this mix. */
  stopWatchingDisplayStream?: () => void;
}

/**
 * `displayStream` is the practitioner's shared telehealth tab, already
 * captured and vetted by the caller. Both the legacy and the continuous start
 * paths accept it, so a telehealth visit cannot silently degrade to
 * microphone-only on one of them.
 */
type StartRecordingOptions = RecordingOptions & {
  deviceId?: string;
  displayStream?: MediaStream;
};

const state: RecorderState = {
  isRecording: false,
  isPaused: false,
  sessionId: null,
  recordingLeaseId: null,
  startTime: 0,
  chunkCounter: { next: 0 },
  takeStartChunkNumber: 0,
  takeGeneration: null,
  serverChunkCursor: null,
  warnedMissingTakeGeneration: false,
  recordedChunks: {},
  mediaRecorder: null,
  recordingStream: null,
  captureResources: null,
  timerInterval: null,
  currentChunkTimeout: null,
  transcriptLinesByChunk: new Map<string, TranscriptLine[]>(),
  snapshotWatermark: null,
  transcriptChunksReceived: new Set<string>(),
  expectedChunkIds: new Set<string>(),
  finalChunkMarked: false,
  transcriptCompletionResolve: null,
  lastCompletionComplete: false,
  lastChunkIdWithBlob: null,
  highestChunkNumberWithBlob: null,
  pendingRecordingCompleteSignal: null,
  pendingFinalSliceSettlements: new Set<Promise<void>>(),
  pendingFinalSliceResolvers: new WeakMap<MediaRecorder, () => void>(),
  pendingFinalSlicePromises: new WeakMap<MediaRecorder, Promise<void>>(),
  latestPendingFinalSliceSettlement: null,
  unsettledSliceRecorders: new WeakSet<MediaRecorder>(),
  recordingOptions: null,
  activeDeviceId: null,
  isSwitchingDevice: false,
  consecutiveArmFailures: 0,
  continuous: null,
  latestContinuousRun: null,
  uploadQueue: null,
  lastContinuousTailSettled: null,
  pausePending: false,
};

const formatChunkId = (chunkNumber: number): string => {
  return chunkNumber.toString().padStart(4, '0');
};

const convertMessageToTranscriptLines = (
  message: TranscriptMessage
): Array<{
  timestamp: number;
  text: string;
  confidence: number;
  chunkId: string;
  segmentId: string;
}> => {
  if (message.segments && message.segments.length > 0) {
    return message.segments.map((segment, index) => ({
      timestamp: segment.start,
      text: segment.text,
      confidence: message.confidence || 0.9,
      chunkId: message.chunkId,
      segmentId: `${message.chunkId}-${index}`,
    }));
  } else if (message.transcription) {
    const chunkNumber = parseInt(message.chunkId, 10);
    const chunkDurationSeconds = CHUNK_DURATION_MS / 1000;
    const startTime = chunkNumber * chunkDurationSeconds;

    return [
      {
        timestamp: startTime,
        text: message.transcription,
        confidence: message.confidence || 0.9,
        chunkId: message.chunkId,
        segmentId: `${message.chunkId}-0`,
      },
    ];
  }
  return [];
};

/**
 * Stored transcript in chunk order. Chunk ids are zero-padded chunk numbers;
 * compare numerically — the padding is only fixed-width up to chunk 9999, so
 * lexicographic order is NOT chunk order past that.
 */
const collectTranscriptLines = (): TranscriptLine[] =>
  [...state.transcriptLinesByChunk.entries()]
    .sort(([a], [b]) => Number(a) - Number(b))
    .flatMap(([, lines]) => lines);

const handleTranscriptMessage = async (
  message: TranscriptMessage
): Promise<void> => {
  const transcriptLines = convertMessageToTranscriptLines(message);

  if (transcriptLines.length > 0) {
    if (state.onSetLiveTranscript) {
      state.onSetLiveTranscript(transcriptLines);
    }

    state.transcriptLinesByChunk.set(message.chunkId, transcriptLines);
  }

  state.transcriptChunksReceived.add(message.chunkId);

  // Non-unified continuous runs hold a chunk's bytes until its transcript
  // proves server durability end to end (AC5); release now.
  state.uploadQueue?.releaseForTranscript(Number(message.chunkId));

  checkTranscriptCompletion();
};

const handleTranscriptSnapshot = (message: TranscriptSnapshotMessage): void => {
  // Delayed frames from an earlier take must never replace the current live
  // transcript. The server only negotiates snapshots for unified takes.
  if (
    state.takeGeneration === null ||
    message.takeGeneration !== state.takeGeneration
  ) {
    return;
  }

  const previous = state.snapshotWatermark;
  // The takeGeneration half is load-bearing on resume: resumeRecording calls
  // setTakeGeneration(newGen) WITHOUT resetTakeState(), so takeGeneration can
  // advance while snapshotWatermark still holds the prior take's higher
  // maxIncludedChunk. The generation comparison accepts the first post-resume
  // snapshot regardless of chunk count; a maxIncludedChunk-only check would
  // wrongly drop it. Do not remove.
  if (
    previous &&
    (message.takeGeneration < previous.takeGeneration ||
      (message.takeGeneration === previous.takeGeneration &&
        message.maxIncludedChunk < previous.maxIncludedChunk))
  ) {
    return;
  }

  const transcriptLines: TranscriptLine[] = message.transcript
    ? [
        {
          timestamp: message.takeStartChunk * (CHUNK_DURATION_MS / 1000),
          text: message.transcript,
          confidence: 0.9,
          chunkId: String(message.maxIncludedChunk),
        },
      ]
    : [];

  state.snapshotWatermark = {
    takeGeneration: message.takeGeneration,
    maxIncludedChunk: message.maxIncludedChunk,
  };
  state.transcriptLinesByChunk = new Map(
    transcriptLines.map((line) => [line.chunkId ?? '', [line]])
  );
  state.onReplaceLiveTranscript?.([...transcriptLines]);
};

const checkTranscriptCompletion = (): void => {
  const allExpectedReceived = Array.from(state.expectedChunkIds).every(
    (chunkId) => state.transcriptChunksReceived.has(chunkId)
  );

  const isComplete = state.finalChunkMarked && allExpectedReceived;

  if (isComplete && state.transcriptCompletionResolve) {
    try {
      state.lastCompletionComplete = true;
      state.transcriptCompletionResolve(collectTranscriptLines());
    } catch (error) {
      logError('audioRecorder', '❌ Error in transcript completion:', error);
    }

    state.transcriptCompletionResolve = null;
  }
};

/**
 * Reports whether the most recent {@link waitForTranscriptCompletion}
 * resolution was complete (gate satisfied) or a timed-out partial. Callers
 * MUST consult this to surface "transcript incomplete" rather than silently
 * proceeding with partial text.
 */
export function getLastTranscriptCompletionStatus(): { complete: boolean } {
  return { complete: state.lastCompletionComplete };
}

export function waitForTranscriptCompletion(): Promise<TranscriptLine[]> {
  return new Promise((resolve) => {
    // NEB-100035: for unified-session (server-stitching) sessions the widget no
    // longer decides transcript completeness — the server assembles from durable
    // chunks. Resolve promptly with the live-captioned lines so the caller can
    // still emit host events / produce a return value, without gating on the
    // client-side finalChunk/allExpectedReceived check. Gate on the ACTUAL take
    // mode, not the raw flag: a failed begin-take leaves takeGeneration null even
    // with the flag on, and that take falls back to the legacy upload path — it
    // must respect the client-side completeness gate below or it would upload a
    // partial transcript (NEB-100347).
    if (
      useRecordingStore.getState().unifiedSessionIdEnabled &&
      state.takeGeneration !== null
    ) {
      state.lastCompletionComplete = true;
      resolve(collectTranscriptLines());
      return;
    }

    const allExpectedReceived = Array.from(state.expectedChunkIds).every(
      (chunkId) => state.transcriptChunksReceived.has(chunkId)
    );

    const isComplete = state.finalChunkMarked && allExpectedReceived;

    if (isComplete) {
      state.lastCompletionComplete = true;
      resolve(collectTranscriptLines());
      return;
    }

    state.transcriptCompletionResolve = resolve;

    setTimeout(() => {
      if (state.transcriptCompletionResolve) {
        // Resolved by the fallback timer: transcript is partial/incomplete.
        state.lastCompletionComplete = false;
        state.transcriptCompletionResolve(collectTranscriptLines());
        state.transcriptCompletionResolve = null;
      }
    }, 5000);
  });
}

async function getAudioStreamWithFallback(
  deviceId?: string
): Promise<MediaStream> {
  const constraints = buildConstraintsFallbackChain(deviceId);

  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: constraints[0],
    });
  } catch (error) {
    if (error instanceof OverconstrainedError) {
      logWarn(
        'audioRecorder',
        'Strict constraints failed, trying fallback approach',
        JSON.stringify(error),
        {
          name: error.name,
          message: error.message,
          constraint: error.constraint,
        }
      );

      let lastError: unknown = error;

      for (let i = 1; i < constraints.length; i++) {
        try {
          return await navigator.mediaDevices.getUserMedia({
            audio: constraints[i],
          });
        } catch (fallbackError) {
          lastError = fallbackError;
          logWarn(
            'audioRecorder',
            'Strict constraints failed, trying fallback approach',
            JSON.stringify(lastError)
          );

          if (i < constraints.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 150));
          }
        }
      }

      throw lastError;
    }

    throw error;
  }
}

function buildConstraintsFallbackChain(
  deviceId?: string
): MediaTrackConstraints[] {
  const hasSpecificDevice = deviceId && deviceId !== 'default';
  const deviceConstraint = hasSpecificDevice
    ? { deviceId: { exact: deviceId } }
    : {};

  const chain: MediaTrackConstraints[] = [
    {
      channelCount: 1,
      sampleRate: 48000,
      echoCancellation: true,
      noiseSuppression: true,
      ...deviceConstraint,
    },
    {
      channelCount: { ideal: 1 },
      echoCancellation: { ideal: true },
      noiseSuppression: { ideal: true },
      ...deviceConstraint,
    },
    deviceConstraint,
  ];

  // Only add completely unconstrained fallback if no specific device selected
  if (!hasSpecificDevice) {
    chain.push({});
  }

  return chain;
}

function stopStreamTracks(stream: MediaStream | undefined): void {
  stream?.getTracks().forEach((track) => track.stop());
}

function releaseCaptureResources(
  resources: AudioCaptureResources | null,
  stopDisplayStream = true
): void {
  if (!resources) return;

  resources.stopWatchingDisplayStream?.();
  stopStreamTracks(resources.mixedStream);
  stopStreamTracks(resources.microphoneStream);
  if (stopDisplayStream) {
    stopStreamTracks(resources.displayStream);
  }
  void resources.audioContext?.close();
}

/**
 * A start that failed before buildCaptureResources stored the mix owns nothing
 * in state.captureResources, so the shared tab the caller handed in would stay
 * live in browser chrome with no way back to it. startRecording never rethrows,
 * so the caller's own cleanup cannot cover this window either.
 */
function releaseUnclaimedDisplayStream(displayStream?: MediaStream): void {
  if (!displayStream) return;
  // Already owned by the live mix: releaseCaptureResources takes it from here.
  if (state.captureResources?.displayStream === displayStream) return;
  stopStreamTracks(displayStream);
}

/**
 * Release the capture chain (microphone, shared telehealth tab, mixer) behind
 * a run's recording stream. That stream is the mix the run was consuming, so
 * it doubles as the ownership key: a run whose mix a newer recording has
 * already replaced stops only the retired stream it still holds, exactly as
 * the pre-mix teardowns did.
 */
function releaseCaptureResourcesForStream(
  stream: MediaStream | null | undefined
): void {
  if (stream && state.captureResources?.mixedStream === stream) {
    releaseCaptureResources(state.captureResources);
    state.captureResources = null;
    return;
  }
  stopStreamTracks(stream ?? undefined);
}

async function buildCaptureResources(
  microphoneStream: MediaStream,
  displayStream?: MediaStream,
  stopDisplayOnFailure = true
): Promise<AudioCaptureResources> {
  if (!displayStream) {
    return {
      microphoneStream,
      mixedStream: microphoneStream,
    };
  }

  if (displayStream.getAudioTracks().length === 0) {
    stopStreamTracks(microphoneStream);
    if (stopDisplayOnFailure) {
      stopStreamTracks(displayStream);
    }
    throw new Error('Telehealth tab audio was not shared');
  }

  let audioContext: AudioContext | undefined;
  try {
    audioContext = new AudioContext();
    const destination = audioContext.createMediaStreamDestination();
    audioContext.createMediaStreamSource(microphoneStream).connect(destination);
    audioContext.createMediaStreamSource(displayStream).connect(destination);
    await audioContext.resume();

    return {
      microphoneStream,
      displayStream,
      mixedStream: destination.stream,
      audioContext,
      stopWatchingDisplayStream: watchSharedTabAudio(displayStream),
    };
  } catch (error) {
    stopStreamTracks(microphoneStream);
    if (stopDisplayOnFailure) {
      stopStreamTracks(displayStream);
    }
    await audioContext?.close();
    throw error;
  }
}

export const SHARED_TAB_ENDED_MESSAGE =
  'The shared telehealth tab stopped sharing audio; recording stopped';

/**
 * The practitioner ended tab sharing mid-visit (Chrome's "Stop sharing"
 * control, or the call tab closing). Treated exactly like microphone loss in
 * ContinuousCutSource: fatal, because the patient's half of the conversation
 * is gone and a note built from the practitioner's half alone is worse than no
 * note. Audio captured before this point is already uploaded and survives.
 */
function handleSharedTabEnded(): void {
  if (!state.isRecording) return;

  const options = state.recordingOptions ?? {};
  logWarn(
    'audioRecorder',
    'Shared telehealth tab stopped delivering audio; ending recording'
  );
  options.onRecordingError?.(new Error(SHARED_TAB_ENDED_MESSAGE));
  void stopRecording(options);
}

function watchSharedTabAudio(displayStream: MediaStream): () => void {
  const teardowns = displayStream.getAudioTracks().map((track) => {
    track.addEventListener('ended', handleSharedTabEnded);
    return () => track.removeEventListener('ended', handleSharedTabEnded);
  });
  return () => teardowns.forEach((teardown) => teardown());
}

/**
 * The production mime chain (AC9): shared verbatim by the legacy per-chunk
 * recorder and both continuous-capture sources so they can never drift.
 */
function selectSupportedMimeType(): string {
  const supportedTypes = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/wav'];

  const mimeType = supportedTypes.find((type) =>
    MediaRecorder.isTypeSupported(type)
  );

  if (!mimeType) {
    throw new Error('No supported audio format found for this browser');
  }

  return mimeType;
}

export async function startRecording(
  options: StartRecordingOptions = {}
): Promise<void> {
  if (state.isRecording) {
    return;
  }

  // A previous recording may have been aborted before its MediaRecorder
  // delivered onstop. Its deferred tail still has an independent settlement
  // callback, but it must not block the stop-time decision for this new take.
  state.latestPendingFinalSliceSettlement = null;

  releaseCaptureResources(state.captureResources);
  state.captureResources = null;
  state.recordingStream = null;

  if (useRecordingStore.getState().unifiedSessionIdEnabled) {
    return startRecordingContinuous(options);
  }

  try {
    const client = WhisperClient.getInstance();

    client.onTranscript = (transcript: TranscriptMessage) => {
      handleTranscriptMessage(transcript).catch((error) => {
        logError('AudioRecorder', 'Error handling transcript message:', error);
      });
    };
    client.onTranscriptSnapshot = handleTranscriptSnapshot;

    if (!state.sessionId || !client.isConnected()) {
      const previousSessionId = state.sessionId;
      state.sessionId = await client.initSession();
      // Chunk numbering is scoped to the transport session: reset only with a
      // fresh session. A preserved session (resetForNewTake, unified-session-id
      // re-record) must keep numbering monotonic or takes would overwrite each
      // other's S3 chunk keys.
      if (state.sessionId !== previousSessionId) {
        state.chunkCounter = { next: 0 };
        // Fresh session: the take also begins at chunk 0 (NEB-100035). Set here
        // too, since this branch does not run resetTakeState.
        state.takeStartChunkNumber = 0;
      }
    }
    // A fresh transport session on a session that already HAS chunks (a reload)
    // would otherwise renumber from 0 and overwrite them. begin-take already ran
    // for this take — prepareSession is awaited before startRecording on this
    // path — so its cursor is available here, before any chunk is numbered.
    liftChunkNumberingToServerCursor(state.chunkCounter.next);

    const microphoneStream = await getAudioStreamWithFallback(options.deviceId);
    const captureResources = await buildCaptureResources(
      microphoneStream,
      options.displayStream
    );

    state.recordedChunks = {};
    state.startTime = Date.now();
    state.isRecording = true;
    state.isPaused = false;
    state.captureResources = captureResources;
    state.recordingStream = captureResources.mixedStream;
    state.recordingOptions = options;
    state.activeDeviceId = options.deviceId ?? 'default';
    state.isSwitchingDevice = false;
    state.consecutiveArmFailures = 0;
    state.transcriptLinesByChunk = new Map<string, TranscriptLine[]>();
    state.lastChunkIdWithBlob = null;
    state.highestChunkNumberWithBlob = null;
    // pendingRecordingCompleteSignal deliberately survives here: it belongs to
    // the PREVIOUS take's unsettled final slice and flushes at that slice's
    // own terminal points (matched by recorder identity).

    state.expectedChunkIds = new Set<string>();
    state.transcriptChunksReceived = new Set<string>();
    state.finalChunkMarked = false;
    state.transcriptCompletionResolve = null;
    state.lastCompletionComplete = false;

    state.onSetLiveTranscript = options.onSetLiveTranscript;
    state.onReplaceLiveTranscript = options.onReplaceLiveTranscript;

    startTimerInterval(options);

    options.onRecordingStarted?.();

    recordNextChunk(options);
  } catch (error) {
    logError('audioRecorder', '❌ Error starting recording:', error);

    state.isRecording = false;

    releaseUnclaimedDisplayStream(options.displayStream);
    releaseCaptureResources(state.captureResources);
    state.captureResources = null;
    state.recordingStream = null;

    if (state.timerInterval) {
      clearInterval(state.timerInterval);
      state.timerInterval = null;
    }

    options.onRecordingError?.(
      error instanceof Error ? error : new Error(String(error))
    );
  }
}

function startTimerInterval(options: RecordingOptions): void {
  if (!options.onTimerUpdate) {
    return;
  }
  state.timerInterval = window.setInterval(() => {
    const elapsedMs = Date.now() - state.startTime;
    options.onTimerUpdate?.(elapsedMs);
  }, 1000);
}

function trackFinalSliceSettlement(sliceRecorder: MediaRecorder): void {
  const settlement = new Promise<void>((resolve) => {
    state.pendingFinalSliceResolvers.set(sliceRecorder, resolve);
  });
  state.pendingFinalSliceSettlements.add(settlement);
  state.pendingFinalSlicePromises.set(sliceRecorder, settlement);
  state.latestPendingFinalSliceSettlement = settlement;
  void settlement.then(() => {
    state.pendingFinalSliceSettlements.delete(settlement);
  });
}

function settleFinalSlice(sliceRecorder: MediaRecorder): void {
  const resolve = state.pendingFinalSliceResolvers.get(sliceRecorder);
  if (!resolve) return;

  const settlement = state.pendingFinalSlicePromises.get(sliceRecorder);
  state.pendingFinalSliceResolvers.delete(sliceRecorder);
  state.pendingFinalSlicePromises.delete(sliceRecorder);
  if (state.latestPendingFinalSliceSettlement === settlement) {
    state.latestPendingFinalSliceSettlement = null;
  }
  resolve();
}

/**
 * Wait until the current stop's non-continuous final slice reaches a terminal
 * point. Older aborted takes may still settle later, but must not block this
 * take. Continuous runs already await their source tail and do not create
 * entries here.
 */
export async function waitForFinalSliceSettlement(): Promise<void> {
  const settlement = state.latestPendingFinalSliceSettlement;
  if (settlement) {
    await settlement;
  }
}

/**
 * Continuous-capture start (flag-on): the microphone opens FIRST and capture
 * begins immediately — zero network round trips on the capture path (AC2).
 * The upload session (prepareSession + initSession) is established
 * concurrently; chunks emitted before it resolves wait in the pending buffer
 * and drain in emit order once it does.
 */
async function startRecordingContinuous(
  options: StartRecordingOptions,
  streamOverride?: MediaStream,
  resumeTakeStartChunkNumber?: number
): Promise<void> {
  try {
    const client = WhisperClient.getInstance();

    client.onTranscript = (transcript: TranscriptMessage) => {
      handleTranscriptMessage(transcript).catch((error) => {
        logError('AudioRecorder', 'Error handling transcript message:', error);
      });
    };
    client.onTranscriptSnapshot = handleTranscriptSnapshot;

    // The previous run's tail may still be flushing; it must be NUMBERED
    // before this take captures its start chunk number, or the late tail
    // would consume the new take's start (re-scope race (a)).
    if (state.lastContinuousTailSettled) {
      await state.lastContinuousTailSettled;
      state.lastContinuousTailSettled = null;
    }

    // Numbering scope matches the legacy path (reset only with a fresh
    // transport session), but is decided BEFORE capture starts: emissions are
    // numbered before initSession resolves.
    if (!state.sessionId) {
      state.chunkCounter = { next: 0 };
    }
    // A resume continues the paused take and therefore carries its original
    // take start. A fresh take starts at the next transport chunk.
    const runFirstChunkNumber = state.chunkCounter.next;
    state.takeStartChunkNumber =
      resumeTakeStartChunkNumber ?? runFirstChunkNumber;

    // A resume hands back the mix this take is already running on, so its
    // capture resources (and the shared tab behind them) must be left intact.
    let stream = streamOverride;
    if (!stream) {
      const microphoneStream = await getAudioStreamWithFallback(
        options.deviceId
      );
      state.captureResources = await buildCaptureResources(
        microphoneStream,
        options.displayStream
      );
      stream = state.captureResources.mixedStream;
    }

    state.recordedChunks = {};
    if (!streamOverride) {
      state.startTime = Date.now();
    }
    state.isRecording = true;
    state.isPaused = false;
    state.recordingStream = stream;
    state.recordingOptions = options;
    state.activeDeviceId = options.deviceId ?? 'default';
    state.isSwitchingDevice = false;
    state.consecutiveArmFailures = 0;
    // A resume continues the paused take, so its transcript accounting must
    // survive; only a fresh take clears it. Conditioned the same way as the
    // take-start chunk number above (resumeTakeStartChunkNumber present = resume).
    if (resumeTakeStartChunkNumber === undefined) {
      state.transcriptLinesByChunk = new Map<string, TranscriptLine[]>();
      state.expectedChunkIds = new Set<string>();
      state.transcriptChunksReceived = new Set<string>();
    }
    state.lastChunkIdWithBlob = null;
    state.highestChunkNumberWithBlob = null;

    state.finalChunkMarked = false;
    state.transcriptCompletionResolve = null;
    state.lastCompletionComplete = false;

    state.onSetLiveTranscript = options.onSetLiveTranscript;
    state.onReplaceLiveTranscript = options.onReplaceLiveTranscript;

    const mimeType = selectSupportedMimeType();
    const cutter = createCutterForMimeType(mimeType);

    // Same transport session (unified re-record) reuses the session's single
    // ordered uploader so chunk numbers upload strictly ascending across
    // takes; clearSession detached any previous session's queue.
    if (!state.uploadQueue) {
      state.uploadQueue = createSessionUploadQueue();
    }

    const continuous: ContinuousCaptureState = {
      // Assigned synchronously on the next statement; no source callback can
      // fire before source.start() below.
      source: null as unknown as CaptureSource,
      mimeType,
      options,
      counter: state.chunkCounter,
      queue: state.uploadQueue,
      takeStartChunkNumber: state.takeStartChunkNumber,
      firstChunkNumber: runFirstChunkNumber,
      sessionState: 'pending',
      takeGeneration: null,
      whisperSessionId: null,
      recordingSessionId: null,
      recordingLeaseId: null,
      highestEnqueuedChunkNumber: null,
      highestUploadedChunkNumber: null,
      outstandingChunks: 0,
      stopped: false,
      degradedToRotating: false,
      finalChunkNumber: null,
      finalDelivered: false,
      terminalHandled: false,
      controlAction: 'stop',
      pauseAcknowledged: false,
      // Assigned synchronously below once establishUploadSession is kicked off;
      // no pause can interleave before then.
      sessionEstablished: null,
    };
    continuous.source = buildContinuousSource(
      continuous,
      options,
      (callbacks) =>
        cutter
          ? new ContinuousCutSource(mimeType, cutter, callbacks)
          : new RotatingOverlapSource(mimeType, callbacks)
    );
    state.continuous = continuous;
    state.latestContinuousRun = continuous;

    continuous.source.start(stream);

    startTimerInterval(options);

    if (!streamOverride) {
      options.onRecordingStarted?.();
    }

    continuous.sessionEstablished = establishUploadSession(continuous, options);
  } catch (error) {
    logError('audioRecorder', '❌ Error starting recording:', error);

    state.isRecording = false;
    state.continuous = null;

    // A failed RESUME leaves the take paused, not falsely recording: restore the
    // paused flags the resume optimistically cleared before the source restart
    // threw (NEB-101501). A failed fresh start has no paused take to fall back
    // to and stays fully stopped.
    if (resumeTakeStartChunkNumber !== undefined) {
      state.isPaused = true;
      useRecordingStore.getState().setIsPaused(true);
    }

    releaseUnclaimedDisplayStream(options.displayStream);
    releaseCaptureResources(state.captureResources);
    state.captureResources = null;
    state.recordingStream = null;

    if (state.timerInterval) {
      clearInterval(state.timerInterval);
      state.timerInterval = null;
    }

    options.onRecordingError?.(
      error instanceof Error ? error : new Error(String(error))
    );
  }
}

/**
 * Wires a capture source to a continuous run. onChunk/onError are bound to THIS
 * source instance (via `continuous.source` identity), so a source retired by a
 * degrade swap can neither double-number a late chunk nor re-enter the error
 * path — only the currently-live source acts.
 */
function buildContinuousSource(
  continuous: ContinuousCaptureState,
  options: RecordingOptions,
  factory: (callbacks: CaptureSourceCallbacks) => CaptureSource
): CaptureSource {
  const callbacks: CaptureSourceCallbacks = {
    onChunk: (chunk: EmittedChunk) => {
      if (continuous.source !== source) {
        return;
      }
      handleContinuousChunk(continuous, chunk);
    },
    onError: (error: Error) =>
      handleContinuousSourceError(continuous, source, error, options),
  };
  // Callbacks capture `source` lazily — they fire only after start(), never
  // during construction, so the reference is bound by then.
  const source = factory(callbacks);
  return source;
}

/**
 * A continuous capture source reported a failure. The FIRST failure of a live
 * ContinuousCutSource degrades in place to the container-agnostic
 * RotatingOverlapSource rather than ending the visit; every other case (a
 * second failure, a failure once already degraded, or a failure while the run
 * is stopping/stopped) takes the original fatal path (onRecordingError +
 * stopRecording, which no-ops the restart once isRecording is already false).
 */
function handleContinuousSourceError(
  continuous: ContinuousCaptureState,
  source: CaptureSource,
  error: Error,
  options: RecordingOptions
): void {
  // Noise from a source we already retired (typically the broken cutter
  // throwing through its own disposal): no longer the live source, so it must
  // not re-enter the fatal path or restart anything.
  if (continuous.source !== source) {
    logWarn(
      'audioRecorder',
      'Ignoring error from a retired continuous capture source',
      error
    );
    return;
  }

  const stream = state.recordingStream;
  if (
    !continuous.degradedToRotating &&
    state.continuous === continuous &&
    state.isRecording &&
    !state.isPaused &&
    !continuous.stopped &&
    stream
  ) {
    degradeToRotating(continuous, source, error, options, stream);
    return;
  }

  logError(
    'audioRecorder',
    'Continuous capture source failed; ending recording',
    error
  );
  options.onRecordingError?.(error);
  void stopRecording(options);
}

/**
 * Swap the failed ContinuousCutSource for a fresh RotatingOverlapSource on the
 * SAME stream and run — same chunk counter, upload queue, and take/session
 * identity — so numbering stays monotonic across the seam. Audio between the
 * last cut this source emitted and the failure is lost, bounded by one chunk
 * duration.
 */
function degradeToRotating(
  continuous: ContinuousCaptureState,
  failedSource: CaptureSource,
  error: Error,
  options: RecordingOptions,
  stream: MediaStream
): void {
  continuous.degradedToRotating = true;
  logError(
    'audioRecorder',
    'Continuous cutter failed mid-recording; degrading to rotating overlap capture',
    {
      mimeType: continuous.mimeType,
      detail: error instanceof Error ? error.message : String(error),
    }
  );

  try {
    const rotating = buildContinuousSource(
      continuous,
      options,
      (callbacks) => new RotatingOverlapSource(continuous.mimeType, callbacks)
    );
    // Set before start so the retired source's late callbacks are ignored.
    continuous.source = rotating;
    rotating.start(stream);
  } catch (startError) {
    logError(
      'audioRecorder',
      'Failed to start rotating fallback after cutter failure; ending recording',
      startError
    );
    options.onRecordingError?.(
      startError instanceof Error ? startError : new Error(String(startError))
    );
    void stopRecording(options);
    return;
  }

  // Dispose the dead source detached: its stop()/flush can throw through the
  // broken cutter, but it is no longer the live source so any error it emits is
  // ignored rather than re-entering the fatal path.
  void Promise.resolve()
    .then(() => failedSource.stop())
    .catch((disposeError) => {
      logWarn(
        'audioRecorder',
        'Error disposing failed continuous capture source during degrade',
        disposeError
      );
    });
}

/**
 * The transport session's single ordered uploader. Every callback reads the
 * chunk's RUN stamps (stamped at enqueue), never live take state, so a chunk
 * draining after the next take starts uploads under its own take's identity
 * (defect 9).
 */
function createSessionUploadQueue(): UploadQueue<ContinuousCaptureState> {
  return new UploadQueue<ContinuousCaptureState>({
    upload: uploadQueuedChunk,
    canUpload: (chunk) => chunk.run.sessionState === 'ready',
    // Release policy (defect 12): the server owns durability from the upload
    // 200 on unified runs; a non-unified (legacy) run holds the bytes until
    // the chunk's transcript arrives.
    releaseOnUpload: (chunk) => chunk.run.takeGeneration !== null,
    onSettled: handleQueuedChunkSettled,
    onDegraded: (chunk) => notifyUploadConnection('degraded', chunk.run),
    onRecovered: (chunk) => notifyUploadConnection('recovered', chunk.run),
    onOnline: handleUploadQueueOnline,
  });
}

/**
 * One upload attempt for a queued chunk; the queue owns retries and
 * classification. captureAgeMs is recomputed immediately before every attempt
 * — a buffered chunk ages during an outage and the server derives
 * capture-time liveness from it. The upload is pinned to the run's own
 * whisper session so a detached run never posts into a newer session.
 */
async function uploadQueuedChunk(
  chunk: QueuedChunk<ContinuousCaptureState>
): Promise<void> {
  const run = chunk.run;
  if (!chunk.blob) {
    throw new Error(
      `Chunk ${chunk.chunkNumber} has no bytes to upload (already released)`
    );
  }
  const captureAgeMs = Math.max(0, Date.now() - chunk.captureEndMs);
  const client = WhisperClient.getInstance();
  const isFinal = chunk.isFinal;
  const takeStart = isFinal ? run.takeStartChunkNumber : undefined;

  let silent: boolean | undefined;
  if (run.takeGeneration !== null) {
    silent = await client.uploadChunk(
      chunk.chunkNumber,
      chunk.blob,
      isFinal,
      takeStart,
      true,
      run.takeGeneration,
      captureAgeMs,
      run.whisperSessionId ?? undefined,
      run.recordingLeaseId ?? undefined
    );
  } else {
    silent = await client.uploadChunk(
      chunk.chunkNumber,
      chunk.blob,
      isFinal,
      takeStart,
      false,
      undefined,
      captureAgeMs,
      run.whisperSessionId ?? undefined
    );
  }
  if (silent !== undefined) {
    run.options.onChunkSilent?.(chunk.chunkNumber, silent);
  }
}

function handleQueuedChunkSettled(
  chunk: QueuedChunk<ContinuousCaptureState>,
  outcome: 'uploaded' | 'failed' | 'dropped',
  error?: unknown
): void {
  const run = chunk.run;
  run.outstandingChunks -= 1;

  if (outcome === 'dropped') {
    const dataLoss = new Error(
      `Audio chunk ${chunk.chunkNumber} was dropped after the upload buffer reached its bound`
    );
    logError('audioRecorder', dataLoss.message, {
      chunkNumber: chunk.chunkNumber,
      bytes: chunk.size,
      error:
        error instanceof Error
          ? error.message
          : error
            ? JSON.stringify(error)
            : '',
    });
    // This is a visible connectivity/data-loss signal, not a reason to tear
    // down an otherwise healthy microphone capture. The dedicated callback
    // lets the host surface the error without invoking the fatal recording
    // callback, which would incorrectly stop the visit.
    run.options.onUploadConnectionChange?.('degraded');
    run.options.onUploadDataLoss?.({
      chunkNumber: chunk.chunkNumber,
      bytes: chunk.size,
      boundBytes: MAX_UNACKED_BUFFER_BYTES,
    });
  }

  if (outcome === 'failed') {
    if (isRecordingConflictError(error)) {
      handleChunkUploadConflict(run);
    } else if (error !== undefined && run.options.onUploadFailed) {
      // A non-retryable server rejection (only these settle as 'failed' with an
      // error), NOT a buffer-bound eviction. Report it distinctly so the host
      // does not misattribute a 4xx to the upload buffer filling up.
      const status = (error as { status?: unknown }).status;
      run.options.onUploadFailed({
        chunkNumber: chunk.chunkNumber,
        bytes: chunk.size,
        status: typeof status === 'number' ? status : undefined,
      });
    } else {
      run.options.onUploadDataLoss?.({
        chunkNumber: chunk.chunkNumber,
        bytes: chunk.size,
        boundBytes: MAX_UNACKED_BUFFER_BYTES,
      });
    }
  }

  if (outcome === 'uploaded') {
    if (chunk.chunkNumber > (run.highestUploadedChunkNumber ?? -1)) {
      run.highestUploadedChunkNumber = chunk.chunkNumber;
    }
    if (chunk.finalDelivered) {
      run.finalDelivered = true;
    }
    // Global transcript-completion state belongs to the newest run only; a
    // detached run's late uploads must not leak into it.
    if (state.latestContinuousRun === run) {
      state.expectedChunkIds.add(formatChunkId(chunk.chunkNumber));
      checkTranscriptCompletion();
    }
  }

  maybeFinishContinuousRun(run);
}

/**
 * A 409 is the server-side recorder-lease fence, not a retryable outage. An
 * unauthenticated upload gets a plain 401 (retried via the token getter), so
 * every 409 on this route is a rival recorder.
 */
function isRecordingConflictError(error: unknown): boolean {
  const failure = error as { status?: unknown } | null;
  return failure?.status === 409;
}

function handleChunkUploadConflict(run: ContinuousCaptureState): void {
  if (run.sessionState === 'failed') {
    return;
  }
  run.sessionState = 'failed';
  const conflict = new SessionRecordingConflictError();
  const droppedChunks = run.queue.failPending(run, conflict);
  logError(
    'audioRecorder',
    'Chunk upload rejected: another computer holds the recorder lease',
    { droppedChunks }
  );
  run.options.onUploadSessionError?.(conflict);
  run.options.onRecordingError?.(conflict);
  void stopRecording(run.options);
}

/**
 * Outage semantics (AC3/AC4): chunk-upload failures during a recording are a
 * connectivity signal, never a recording error — onRecordingError is wired to
 * fatal UI paths that stop the recording. Surfaced only while recording; a
 * post-stop background drain retries silently (counters + logs only).
 *
 * `run` is the run of the chunk that triggered the transition, NOT live take
 * state: the queue is shared across takes, so a detached older run's chunk must
 * report to its own callbacks.
 */
function notifyUploadConnection(
  status: 'degraded' | 'recovered',
  run: ContinuousCaptureState
): void {
  if (!state.isRecording) {
    return;
  }
  run.options.onUploadConnectionChange?.(status);
}

/**
 * Connectivity recovery: re-init the WS transport — its automatic reconnects
 * are capped, so a long outage exhausts them and nothing else would reopen
 * the socket for transcript pushes. Transport/liveness only: reconnecting
 * does NOT restore server-side sweep state cleared during the outage.
 */
function handleUploadQueueOnline(): void {
  WhisperClient.getInstance()
    .reconnectTransport()
    .catch((error) => {
      logWarn(
        'audioRecorder',
        'WS transport re-init after online failed',
        String(error)
      );
    });
}

/**
 * Lift legacy numbering to begin-take's authoritative session cursor. A
 * reloaded browser starts its local counter at zero even when the durable
 * session already owns chunks; moving the counter and take start together
 * preserves the server's contiguous-take contract.
 */
function liftChunkNumberingToServerCursor(firstChunkNumber: number): number {
  const cursor = state.serverChunkCursor;
  if (cursor === null) {
    return 0;
  }
  const offset = cursor - firstChunkNumber;
  if (offset <= 0) {
    return 0;
  }
  state.chunkCounter.next += offset;
  state.takeStartChunkNumber += offset;
  logWarn('audioRecorder', 'Lifted chunk numbering to the server cursor', {
    offset,
    takeStartChunkNumber: state.takeStartChunkNumber,
  });
  return offset;
}

/** Apply the same cursor floor to mic-first chunks already waiting in queue. */
function liftRunChunkNumberingToServerCursor(
  run: ContinuousCaptureState
): void {
  if (state.chunkCounter !== run.counter) {
    return;
  }
  const offset = liftChunkNumberingToServerCursor(run.firstChunkNumber);
  if (offset === 0) {
    return;
  }
  run.queue.shiftRunChunkNumbers(run, offset);
  run.firstChunkNumber += offset;
  run.takeStartChunkNumber += offset;
  if (run.highestEnqueuedChunkNumber !== null) {
    run.highestEnqueuedChunkNumber += offset;
  }
  if (run.finalChunkNumber !== null) {
    run.finalChunkNumber += offset;
  }
}

/**
 * Runs the network half of a continuous-capture start off the capture path:
 * host session prep (createSession/begin-take via prepareSession), then the
 * whisper transport session. Success stamps the run's upload identity and
 * releases the queue. Failure is loud and ends the recording — with no
 * session nothing buffered can ever upload.
 */
async function establishUploadSession(
  continuous: ContinuousCaptureState,
  options: RecordingOptions
): Promise<void> {
  try {
    await options.prepareSession?.();
    const client = WhisperClient.getInstance();
    if (!state.sessionId || !client.isConnected()) {
      state.sessionId = await client.initSession();
    }
    const whisperSessionId = state.sessionId;

    const { recordingSessionId, unifiedSessionIdEnabled } =
      useRecordingStore.getState();

    // begin-take has now resolved, so queued mic-first chunks can be lifted
    // before the queue becomes uploadable.
    liftRunChunkNumberingToServerCursor(continuous);

    // The run's upload identity, settled exactly once: uploads and the
    // recording-complete fallback read these stamps, never live take state.
    continuous.whisperSessionId = whisperSessionId;
    continuous.recordingSessionId = recordingSessionId || null;
    continuous.recordingLeaseId = state.recordingLeaseId;
    continuous.takeGeneration = unifiedTakeGeneration(unifiedSessionIdEnabled);
    continuous.sessionState = 'ready';

    if (whisperSessionId) {
      options.onUploadSessionReady?.(whisperSessionId);
    }
    continuous.queue.kick();
    maybeFinishContinuousRun(continuous);
  } catch (error) {
    continuous.sessionState = 'failed';
    const droppedChunks = continuous.queue.failPending(continuous, error);
    logError(
      'audioRecorder',
      'Failed to establish upload session for continuous capture; ending recording',
      { droppedChunks, error: String(error) }
    );
    const sessionError =
      error instanceof Error ? error : new Error(String(error));
    options.onUploadSessionError?.(sessionError);
    options.onRecordingError?.(sessionError);
    void stopRecording(options);
  }
}

/**
 * Emission handler shared by both capture sources. Chunk numbers are assigned
 * here, at EMIT time — the same contract as the legacy path: an emission
 * dropped for size consumes no number, so each take's numbers stay 0-based
 * and contiguous for the server's completeness gate. Numbering comes from the
 * RUN's own counter, so a late tail flushing after clearSession numbers
 * against its own session, never the new one.
 */
function handleContinuousChunk(
  continuous: ContinuousCaptureState,
  chunk: EmittedChunk
): void {
  if (chunk.blob.size < WHISPER_CONFIG.minChunkSizeBytes) {
    logWarn('audioRecorder', 'Dropped audio slice', {
      reason: 'blob_too_small',
      size: chunk.blob.size,
    });
    return;
  }
  if (continuous.sessionState === 'failed') {
    logError(
      'audioRecorder',
      'Dropped audio chunk: upload session could not be established',
      { size: chunk.blob.size }
    );
    return;
  }

  const chunkNumber = continuous.counter.next++;
  continuous.highestEnqueuedChunkNumber = chunkNumber;
  continuous.outstandingChunks += 1;
  continuous.queue.enqueue({
    chunkNumber,
    blob: chunk.blob,
    size: chunk.blob.size,
    captureEndMs: chunk.captureEndMs,
    run: continuous,
  });
}

/**
 * Terminal point of a continuous run: the stop path has marked the tail AND
 * every enqueued chunk has settled (uploaded / failed non-retryably / dropped
 * at the bound). Owns the recording-complete fallback signal.
 *
 * CONTRACT (stale_expected_count): the fallback fires only HERE, with
 * finalChunkNumber = the highest chunk of this run that ACTUALLY persisted.
 * The server pins expectedChunkCount = finalChunkNumber + 1, and
 * finalizeStitchedTranscript permanently skips (stale_expected_count) once
 * maxPersistedChunk >= expectedChunkCount — so signalling before the queue is
 * terminal, or signalling what the client merely INTENDS to deliver, bricks
 * the session as soon as a queued chunk drains past the signalled count. At
 * terminal the run's persisted set is final — nothing of this run uploads
 * afterward — so the signalled count is exact and the finalize gate is
 * satisfiable. A queue that never reaches terminal (an unending retryable
 * outage) sends nothing; the server sweep is the backstop.
 */
async function postRunControlSignal(
  run: ContinuousCaptureState
): Promise<void> {
  if (run.takeGeneration === null || !run.recordingSessionId) return;

  if (run.controlAction === 'pause') {
    if (!run.recordingLeaseId) return;
    await sessionsClient.postRecordingComplete(run.recordingSessionId, {
      leaseId: run.recordingLeaseId,
      leaseTakeGeneration: run.takeGeneration,
      action: 'pause',
    });
    return;
  }

  if (!run.recordingLeaseId && run.finalDelivered) return;
  const request = {
    ...(run.highestUploadedChunkNumber !== null && {
      finalChunkNumber: run.highestUploadedChunkNumber,
      takeStart: run.takeStartChunkNumber,
      takeGeneration: run.takeGeneration,
    }),
    ...(run.recordingLeaseId && {
      leaseId: run.recordingLeaseId,
      leaseTakeGeneration: run.takeGeneration,
      action: 'stop' as const,
    }),
  };
  try {
    await sessionsClient.postRecordingComplete(run.recordingSessionId, request);
  } catch (error) {
    logError(
      'audioRecorder',
      'recording-complete stop failed; retrying lease release once',
      error instanceof Error ? error.message : String(error)
    );
    try {
      await sessionsClient.postRecordingComplete(
        run.recordingSessionId,
        request
      );
    } catch (retryError) {
      logError(
        'audioRecorder',
        'recording-complete stop retry failed; recorder lease may remain held',
        retryError instanceof Error ? retryError.message : String(retryError)
      );
    }
  }
}

function maybeFinishContinuousRun(continuous: ContinuousCaptureState): void {
  if (
    !continuous.stopped ||
    continuous.outstandingChunks > 0 ||
    continuous.terminalHandled ||
    continuous.sessionState === 'pending'
  ) {
    return;
  }
  // A pause is not durable until the server acknowledges its checkpoint.
  // In particular, do not let a session-ready callback or an empty queue
  // race the awaited pause path and emit a premature terminal signal.
  if (continuous.controlAction === 'pause' && !continuous.pauseAcknowledged) {
    return;
  }
  continuous.terminalHandled = true;
  void postRunControlSignal(continuous).catch((error) => {
    logError(
      'audioRecorder',
      'recording-complete failed at continuous run terminal',
      error instanceof Error ? error.message : String(error)
    );
  });

  if (
    state.latestContinuousRun === continuous &&
    (!state.isRecording || state.isPaused)
  ) {
    state.finalChunkMarked = true;
    checkTranscriptCompletion();
  }
}

async function pauseRecordingContinuous(
  continuous: ContinuousCaptureState
): Promise<void> {
  if (continuous.stopped) return;

  if (state.timerInterval) {
    clearInterval(state.timerInterval);
    state.timerInterval = null;
  }

  continuous.controlAction = 'pause';
  state.pausePending = true;

  let pauseCheckpointAttempted = false;
  try {
    // hold + stop live inside the try so a synchronous throw from either still
    // reaches the catch's releaseHold — otherwise the queue stays held forever.
    continuous.queue.hold();
    const tailSettled = continuous.source.stop();
    state.lastContinuousTailSettled = tailSettled.then(
      () => undefined,
      () => undefined
    );
    if (state.continuous === continuous) {
      state.continuous = null;
    }
    await tailSettled;
    continuous.stopped = true;
    continuous.queue.releaseHold();

    if (continuous.controlAction === 'pause') {
      // A pause fired before begin-take/initSession resolved has no take
      // generation or recordingSessionId yet, so postRunControlSignal would
      // silently no-op — leaving the take with no server checkpoint and the
      // lease never released. Wait for the session to settle first so the
      // checkpoint and lease release actually happen once the run has an
      // identity. Mirrors the stop path's pending guard.
      if (
        continuous.sessionState === 'pending' &&
        continuous.sessionEstablished
      ) {
        await continuous.sessionEstablished;
      }

      // A stop can supersede the pause while the session was establishing; if
      // so, fall through to the stop-owns-terminal path below.
      if (continuous.controlAction === 'pause') {
        // Server confirms first: durably checkpoint the take BEFORE the UI
        // enters the paused state, so a failed checkpoint never strands the
        // user in a "paused" view of a session that was never saved.
        pauseCheckpointAttempted = true;
        await postRunControlSignal(continuous);
        state.isPaused = true;
        useRecordingStore.getState().setIsPaused(true);
        continuous.pauseAcknowledged = true;
        continuous.terminalHandled = true;
        state.pausePending = false;
        if (
          state.latestContinuousRun === continuous &&
          (!state.isRecording || state.isPaused)
        ) {
          state.finalChunkMarked = true;
          checkTranscriptCompletion();
        }
        return;
      }
    }

    // A stop can race the pause tail flush (or supersede it while the session
    // was still establishing). In that case the pause request is intentionally
    // superseded and the stop signal owns terminal handling.
    state.pausePending = false;
    maybeFinishContinuousRun(continuous);
  } catch (error) {
    continuous.queue.releaseHold();
    state.pausePending = false;
    continuous.pauseAcknowledged = false;
    const recordingError =
      error instanceof Error ? error : new Error(String(error));

    // A rejected pause checkpoint is recoverable, not terminal: the paused UI
    // was never entered (isPaused flips only after a durable checkpoint), so
    // keep the session recording and surface a retriable pause error instead of
    // the fatal onRecordingError teardown that silently kills the recording.
    if (
      (continuous.controlAction as 'pause' | 'stop') === 'pause' &&
      pauseCheckpointAttempted
    ) {
      startTimerInterval(continuous.options);
      continuous.options.onPauseCheckpointError?.(recordingError);
      return;
    }

    // Otherwise the recording is ending (a stop raced the pause, or the capture
    // tail failed to flush): tear the session down and surface the fatal error.
    state.isRecording = false;
    state.isPaused = false;
    releaseCaptureResourcesForStream(state.recordingStream);
    state.recordingStream = null;
    state.activeDeviceId = null;
    useRecordingStore.getState().setIsPaused(false);
    if (pauseCheckpointAttempted) {
      continuous.options.onPauseCheckpointError?.(recordingError);
    }
    continuous.options.onRecordingError?.(recordingError);
    if ((continuous.controlAction as 'pause' | 'stop') === 'stop') {
      maybeFinishContinuousRun(continuous);
    }
  }
}

async function stopFromPausedContinuous(
  options: RecordingOptions
): Promise<void> {
  const pausedRun = state.latestContinuousRun;

  state.isRecording = false;
  state.isPaused = false;
  releaseCaptureResourcesForStream(state.recordingStream);
  state.recordingStream = null;
  // Tear down the same recorder state stopRecordingContinuous clears, so the
  // two stop paths converge and a paused-then-stopped session leaves no stale
  // device/options/recorder behind. (latestContinuousRun is intentionally kept:
  // it is read below and by the run's terminal handling, matching the other
  // stop path, which also does not null it.)
  state.continuous = null;
  state.isSwitchingDevice = false;
  state.recordingOptions = null;
  state.activeDeviceId = null;
  state.mediaRecorder = null;
  useRecordingStore.getState().setIsPaused(false);

  if (pausedRun) {
    pausedRun.controlAction = 'stop';
    await state.lastContinuousTailSettled;
    await pausedRun.queue.waitForRunSettled(pausedRun);
    if (pausedRun.terminalHandled) {
      state.pausePending = false;
      await postRunControlSignal(pausedRun).catch((error) => {
        logError(
          'audioRecorder',
          'recording-complete failed at continuous run terminal',
          error instanceof Error ? error.message : String(error)
        );
      });
    } else {
      state.pausePending = false;
      maybeFinishContinuousRun(pausedRun);
    }
  }

  options.onRecordingStopped?.();
}

/**
 * Continuous-capture stop: stops capture, numbers and final-marks the tail,
 * and resolves — no network work (defect 8). The queue delivers the backlog
 * (the tail's first upload carrying ?final=true + takeStart) in the
 * background, retrying across outages; the run's terminal point owns the
 * recording-complete fallback. onRecordingStopped therefore fires without
 * awaiting the drain, and a chunk failure after stop surfaces via counters,
 * never onRecordingError.
 */
async function stopRecordingContinuous(
  continuous: ContinuousCaptureState,
  options: RecordingOptions
): Promise<void> {
  state.isRecording = false;
  state.isPaused = false;
  continuous.controlAction = 'stop';
  // Captured before any await: a NEW recording can start while this stop's
  // queue is still draining and swap in its own stream and continuous state;
  // every teardown below must touch only what THIS run still owns.
  const stream = state.recordingStream;

  try {
    if (state.timerInterval) {
      clearInterval(state.timerInterval);
      state.timerInterval = null;
    }

    // Hold the queue until the tail emission is numbered AND marked final —
    // otherwise it could upload without ?final=true in the gap.
    continuous.queue.hold();
    const tailSettled = continuous.source.stop();
    state.lastContinuousTailSettled = tailSettled.then(
      () => undefined,
      () => undefined
    );
    await tailSettled;

    releaseCaptureResourcesForStream(stream);
    if (state.recordingStream === stream) {
      state.recordingStream = null;
    }

    continuous.stopped = true;
    if (continuous.highestEnqueuedChunkNumber !== null) {
      continuous.finalChunkNumber = continuous.highestEnqueuedChunkNumber;
      continuous.queue.markFinal(continuous.finalChunkNumber);
    }
    continuous.queue.releaseHold();

    const ownsRecorderState = state.continuous === continuous;
    if (ownsRecorderState) {
      state.continuous = null;
      state.isSwitchingDevice = false;
      state.recordingOptions = null;
      state.activeDeviceId = null;

      useRecordingStore.getState().setIsPaused(false);
    }

    // Already terminal when nothing is outstanding (e.g. a fully drained or
    // empty take); otherwise the last settling chunk triggers it.
    maybeFinishContinuousRun(continuous);

    options.onRecordingStopped?.();
  } catch (error) {
    continuous.stopped = true;
    continuous.queue.releaseHold();
    releaseCaptureResourcesForStream(stream);
    if (state.continuous === continuous) {
      state.continuous = null;
      state.isSwitchingDevice = false;
      state.recordingOptions = null;
      state.activeDeviceId = null;
      state.mediaRecorder = null;
      if (state.recordingStream === stream) {
        state.recordingStream = null;
      }

      useRecordingStore.getState().setIsPaused(false);
    }

    // A source that failed before any chunk emitted leaves the queue with
    // nothing to settle it, so the terminal work (recording-complete, final
    // marker, completion gate) would never run. Settle it here too (mirrors
    // the try path) now that the run is marked stopped.
    maybeFinishContinuousRun(continuous);

    options.onRecordingError?.(
      error instanceof Error ? error : new Error(String(error))
    );
  }
}

function recordNextChunk(options: RecordingOptions): void {
  if (!state.isRecording || state.isPaused || !state.recordingStream) {
    return;
  }

  try {
    const mimeType = selectSupportedMimeType();

    const chunkRecorder = new MediaRecorder(state.recordingStream, {
      mimeType: mimeType,
      audioBitsPerSecond: 128000,
    });

    state.mediaRecorder = chunkRecorder;

    const chunks: Blob[] = [];

    chunkRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    chunkRecorder.onstop = () => {
      // Identity de-dup guard (NEB-91363): only the CURRENT recorder may re-arm.
      // A recorder stopped by switchDevice is no longer state.mediaRecorder, so
      // its async onstop must not re-arm — switchDevice owns the single re-arm.
      const canReArm =
        state.isRecording &&
        !state.isPaused &&
        state.mediaRecorder === chunkRecorder;

      const blob =
        chunks.length > 0 ? new Blob(chunks, { type: mimeType }) : null;

      if (!blob || blob.size < WHISPER_CONFIG.minChunkSizeBytes) {
        logWarn('audioRecorder', 'Dropped audio slice', {
          reason: blob ? 'blob_too_small' : 'no_data',
          size: blob ? blob.size : 0,
        });
        state.unsettledSliceRecorders.delete(chunkRecorder);
        settleFinalSlice(chunkRecorder);
        if (canReArm) {
          recordNextChunk(options);
        } else if (!state.isRecording) {
          state.finalChunkMarked = true;
          checkTranscriptCompletion();
          // The slice was never uploaded — signal the last chunk that was.
          flushDeferredRecordingComplete(
            chunkRecorder,
            state.highestChunkNumberWithBlob
          );
        }
        return;
      }

      // Chunk numbers are assigned at EMIT time, never at arm time (NEB-100347):
      // the server's stitching contract requires each take's numbers to be
      // 0-based and contiguous, so a slice that yields no usable blob must
      // consume no number. One hole makes the completeness gate unsatisfiable
      // and the take force-finalizes as a false `partial` — including a slice
      // retired by switchDevice, which the old arm-time reclaim could not cover.
      const chunkNumber = state.chunkCounter.next++;

      void processRecordedChunk(
        chunkRecorder,
        chunkNumber,
        blob,
        options
      ).finally(() => settleFinalSlice(chunkRecorder));

      if (canReArm) {
        setTimeout(() => {
          recordNextChunk(options);
        }, 100);
      }
    };

    chunkRecorder.start(1000);
    // Added only after start() succeeds — a recorder that never started has no
    // onstop coming to settle it.
    state.unsettledSliceRecorders.add(chunkRecorder);

    state.consecutiveArmFailures = 0;

    state.currentChunkTimeout = window.setTimeout(() => {
      if (chunkRecorder.state === 'recording') {
        chunkRecorder.stop();
      }
    }, CHUNK_DURATION_MS);
  } catch (error) {
    state.consecutiveArmFailures += 1;

    // A transient failure to arm the next chunk (common right after a live device
    // swap) is recoverable: re-arm WITHOUT surfacing a fatal onRecordingError, which
    // the UI treats as terminal and would wedge recording (NEB-99247). Bound the
    // retries so a genuinely permanent failure surfaces instead of looping silently.
    if (
      state.isRecording &&
      !state.isPaused &&
      state.consecutiveArmFailures <= MAX_CONSECUTIVE_ARM_FAILURES
    ) {
      logWarn(
        'audioRecorder',
        'Failed to arm next chunk; retrying without ending the recording',
        error
      );
      setTimeout(() => {
        recordNextChunk(options);
      }, 1000);
    } else {
      logError(
        'audioRecorder',
        'Failed to arm next chunk after exhausting retries; ending recording',
        error
      );
      options.onRecordingError?.(
        error instanceof Error ? error : new Error(String(error))
      );
      void stopRecording(options);
    }
  }
}

/**
 * Live-swaps the active recording to a new input device WITHOUT ending the
 * session, chunk numbering, or whisper socket. No-op unless actively recording.
 *
 * Ordering is deliberate: the NEW stream is acquired FIRST so the working
 * recording is never torn down on a failed acquisition. On failure the swap
 * aborts and recording continues on the prior device, with the error surfaced
 * via onRecordingError — never a silent fallback to a dead/old stream.
 */
export async function switchDevice(deviceId: string): Promise<void> {
  if (!state.isRecording || state.isPaused) return;
  // A swap is already in flight: throw so the caller can revert its device
  // tracking and retry once the in-flight swap settles (NEB-91363, N1).
  if (state.isSwitchingDevice) {
    throw new Error('A device switch is already in progress');
  }
  if ((deviceId ?? 'default') === (state.activeDeviceId ?? 'default')) return;

  state.isSwitchingDevice = true;

  let newStream: MediaStream;
  try {
    newStream = await getAudioStreamWithFallback(deviceId); // acquire FIRST
  } catch (error) {
    state.isSwitchingDevice = false;
    const swapError = error instanceof Error ? error : new Error(String(error));
    // Use the non-stopping surface: recording is still alive on the old device.
    state.recordingOptions?.onSwitchDeviceError?.(swapError);
    // Rethrow so the caller can revert its device tracking and allow a retry;
    // onSwitchDeviceError already surfaced the single user-facing error.
    throw swapError; // recording continues on the old device — no silent dead-stream fallback
  }

  let nextCaptureResources: AudioCaptureResources;
  try {
    nextCaptureResources = await buildCaptureResources(
      newStream,
      state.captureResources?.displayStream,
      false
    );
  } catch (error) {
    state.isSwitchingDevice = false;
    const swapError = error instanceof Error ? error : new Error(String(error));
    state.recordingOptions?.onSwitchDeviceError?.(swapError);
    throw swapError;
  }

  // Recording was stopped while acquiring the new stream: the new stream is now
  // orphaned (clearSession/stopRecording cannot reach it), so stop its tracks
  // here to release the mic and abort the swap (NEB-91363, B).
  if (!state.isRecording) {
    releaseCaptureResources(nextCaptureResources);
    state.isSwitchingDevice = false;
    return;
  }

  // Continuous capture: the source owns the swap — it retires the current
  // recorder (emitting its tail chunk) and continues on the new MIX, never the
  // bare microphone: on a telehealth visit the mix is what carries the shared
  // tab, and handing the source the raw microphone would silently drop the
  // patient's half from every chunk after the swap (NEB-102775). The swap seam
  // is a genuine discontinuity; numbering continues monotonically.
  if (state.continuous) {
    const retiredCaptureResources = state.captureResources;
    const retiredStream = state.recordingStream;
    state.captureResources = nextCaptureResources;
    state.recordingStream = nextCaptureResources.mixedStream;
    state.activeDeviceId = deviceId ?? 'default';
    try {
      await state.continuous.source.switchStream(
        nextCaptureResources.mixedStream
      );
    } catch (error) {
      logError('audioRecorder', 'Continuous device switch failed', error);
      const swapError =
        error instanceof Error ? error : new Error(String(error));
      // Same non-stopping surface as the legacy acquisition-failure path: the
      // caller's .catch only reverts device tracking, so without this the user
      // gets NO signal.
      state.recordingOptions?.onSwitchDeviceError?.(swapError);
      throw swapError;
    } finally {
      // The shared tab moved over to the rebuilt mix, so the retired mix must
      // not stop it. A run that never owned a mix only holds its raw stream.
      if (retiredCaptureResources) {
        releaseCaptureResources(retiredCaptureResources, false);
      } else {
        stopStreamTracks(retiredStream ?? undefined);
      }
      state.isSwitchingDevice = false;
    }
    return;
  }

  // Finalize the in-flight chunk. Its onstop uploads the already-captured audio
  // and will NOT re-arm: the identity guard sees it is no longer the active
  // recorder once we swap in the new chunk recorder below.
  if (state.currentChunkTimeout) {
    clearTimeout(state.currentChunkTimeout);
    state.currentChunkTimeout = null;
  }
  if (state.mediaRecorder && state.mediaRecorder.state === 'recording') {
    state.mediaRecorder.stop();
  }

  const oldCaptureResources = state.captureResources;
  state.captureResources = nextCaptureResources;
  state.recordingStream = nextCaptureResources.mixedStream;
  state.activeDeviceId = deviceId ?? 'default';
  releaseCaptureResources(oldCaptureResources, false);

  state.isSwitchingDevice = false;
  if (state.recordingOptions) {
    recordNextChunk(state.recordingOptions);
  }
}

/**
 * Take generation to send with an upload, or `null` to use the LEGACY upload
 * path (NEB-100347).
 *
 * The server mints the generation in `POST begin-take`, and whisperClient throws
 * `takeGeneration is required for unified stitching` without it. So a transient
 * begin-take failure must NOT be treated as "unified anyway": that combination
 * makes every upload throw client-side and no audio reaches the server at all.
 * Falling back to the legacy path keeps the recording usable — the audio lands,
 * the server sees no unified stamp and handles the session as flag-off, and the
 * only thing lost is server-side stitching for this take.
 */
function unifiedTakeGeneration(
  unifiedSessionIdEnabled: boolean
): number | null {
  if (!unifiedSessionIdEnabled) {
    return null;
  }
  if (state.takeGeneration === null) {
    if (!state.warnedMissingTakeGeneration) {
      state.warnedMissingTakeGeneration = true;
      logWarn(
        'audioRecorder',
        'Unified stitching is enabled but no take generation was stamped; uploading via the legacy path',
        { sessionId: state.sessionId }
      );
    }
    return null;
  }
  return state.takeGeneration;
}

async function processRecordedChunk(
  sliceRecorder: MediaRecorder,
  chunkNumber: number,
  blob: Blob,
  options: RecordingOptions
): Promise<void> {
  if (!state.sessionId) {
    state.unsettledSliceRecorders.delete(sliceRecorder);
    return;
  }

  try {
    const chunkId = formatChunkId(chunkNumber);

    const chunk: AudioChunk = {
      id: chunkId,
      blob,
      status: 'uploading',
      timestamp: Date.now(),
      size: blob.size,
    };

    state.recordedChunks[chunkId] = chunk;

    const client = WhisperClient.getInstance();

    try {
      const { unifiedSessionIdEnabled } = useRecordingStore.getState();

      const takeGeneration = unifiedTakeGeneration(unifiedSessionIdEnabled);
      let uploadAttempts = 0;
      let silent: boolean | undefined;

      /** Retry transient slice failures without reaching terminal onRecordingError. */
      while (true) {
        uploadAttempts += 1;
        try {
          if (takeGeneration !== null) {
            silent = await client.uploadChunk(
              chunkNumber,
              blob,
              false,
              undefined,
              true,
              takeGeneration
            );
          } else {
            silent = await client.uploadChunk(chunkNumber, blob, false);
          }
          break;
        } catch (error) {
          if (
            !state.isRecording ||
            uploadAttempts >= UNAUTHORIZED_MAX_ATTEMPTS ||
            !isRetryableUploadError(error, uploadAttempts)
          ) {
            throw error;
          }

          const delay = Math.min(
            BACKOFF_BASE_MS * 2 ** (uploadAttempts - 1),
            BACKOFF_MAX_MS
          );
          let remaining = delay;
          while (state.isRecording && remaining > 0) {
            const wait = Math.min(100, remaining);
            await new Promise<void>((resolve) => setTimeout(resolve, wait));
            remaining -= wait;
          }
          if (!state.isRecording) {
            throw error;
          }
        }
      }

      if (silent !== undefined) {
        options.onChunkSilent?.(chunkNumber, silent);
      }

      chunk.status = 'uploaded';
      state.unsettledSliceRecorders.delete(sliceRecorder);

      state.expectedChunkIds.add(chunkId);

      // In-flight final slice: now that it is expected, mark so the gate trips
      // once its transcript arrives instead of racing ahead of it.
      if (!state.isRecording) {
        state.finalChunkMarked = true;
        checkTranscriptCompletion();
        // The slice's blob reached the server — it IS the final chunk.
        flushDeferredRecordingComplete(sliceRecorder, chunkNumber);
      }

      // Release memory: keep only the highest-numbered chunk's blob for potential finalization
      const previousLatestChunkId = state.lastChunkIdWithBlob;
      const previousHighestChunkNumber = state.highestChunkNumberWithBlob ?? -1;

      if (chunkNumber > previousHighestChunkNumber) {
        if (
          previousLatestChunkId &&
          previousLatestChunkId !== chunkId &&
          state.recordedChunks[previousLatestChunkId]?.status === 'uploaded'
        ) {
          delete state.recordedChunks[previousLatestChunkId];
        }

        state.lastChunkIdWithBlob = chunkId;
        state.highestChunkNumberWithBlob = chunkNumber;
      } else if (chunkId !== state.lastChunkIdWithBlob) {
        const currentChunk = state.recordedChunks[chunkId];
        if (currentChunk?.status === 'uploaded') {
          delete state.recordedChunks[chunkId];
        }
      }
    } catch (error) {
      // The slice's upload failed, so its bytes are NOT on the server — signal
      // the last chunk that actually uploaded instead. Settle BEFORE the error
      // callback: a reentrant stopRecording must see this slice as terminal.
      state.unsettledSliceRecorders.delete(sliceRecorder);
      flushDeferredRecordingComplete(
        sliceRecorder,
        state.highestChunkNumberWithBlob
      );
      options.onRecordingError?.(
        error instanceof Error ? error : new Error(String(error))
      );
      return;
    }
  } catch (error) {
    state.unsettledSliceRecorders.delete(sliceRecorder);
    const chunkId = formatChunkId(chunkNumber);
    if (state.recordedChunks[chunkId]) {
      state.recordedChunks[chunkId].status = 'failed';
    }

    options.onRecordingError?.(
      error instanceof Error ? error : new Error(String(error))
    );
  }
}

/**
 * Terminal point of a final slice that had not settled at stop time
 * (NEB-100347): fire the recording-complete signal that stopRecording deferred.
 * Matched by recorder identity — a dropped slice has no chunk number, and a
 * recorder retired by switchDevice must not flush a signal deferred for its
 * successor. `uploadedFinalChunkNumber` must be a chunk that actually reached
 * the server — the slice's own number when its blob uploaded, otherwise the
 * highest streamed chunk — never a number the server may not receive, or the
 * server would wait for a chunk that never arrives and force-finalize a false
 * partial. `null` (nothing uploaded this recording) skips the signal; the
 * server sweep is the backstop. Failures are logged, never surfaced — same as
 * the immediate no-blob post in stopRecording.
 */
function flushDeferredRecordingComplete(
  sliceRecorder: MediaRecorder,
  uploadedFinalChunkNumber: number | null
): void {
  const pending = state.pendingRecordingCompleteSignal;
  if (!pending || pending.sliceRecorder !== sliceRecorder) {
    return;
  }
  state.pendingRecordingCompleteSignal = null;

  if (uploadedFinalChunkNumber === null) {
    logError(
      'audioRecorder',
      'Cannot send deferred recording-complete: no chunk was uploaded',
      { recordingSessionId: pending.recordingSessionId }
    );
    return;
  }

  void sessionsClient
    .postRecordingComplete(pending.recordingSessionId, {
      finalChunkNumber: uploadedFinalChunkNumber,
      takeStart: pending.takeStart,
      takeGeneration: pending.takeGeneration,
    })
    .catch((completeError) => {
      logError(
        'audioRecorder',
        'deferred recording-complete failed at final slice terminal',
        completeError instanceof Error
          ? completeError.message
          : String(completeError)
      );
    });
}

export async function stopRecording(
  options: RecordingOptions = {}
): Promise<void> {
  if (!state.isRecording) {
    return;
  }

  if (state.continuous) {
    return stopRecordingContinuous(state.continuous, options);
  }

  if (useRecordingStore.getState().unifiedSessionIdEnabled && state.isPaused) {
    return stopFromPausedContinuous(options);
  }

  // Non-null when the current slice has not SETTLED at stop time — still
  // capturing, or stopped (12s seam / pause) with its onstop still queued;
  // that onstop fires after isRecording is cleared and owns flushing the
  // deferred signal. `.state` alone cannot detect the stopped-but-unsettled
  // window: treating it as "no slice in flight" would ?final=true the
  // PREVIOUS chunk while the pending onstop emits one more, making every
  // finalize skip as stale_expected_count.
  const inFlightSliceRecorder =
    state.mediaRecorder &&
    state.unsettledSliceRecorders.has(state.mediaRecorder)
      ? state.mediaRecorder
      : null;

  // MediaRecorder.stop() queues onstop; register the barrier before invoking
  // it so even implementations that dispatch the event synchronously cannot
  // race the consumer's stop-time audio check.
  if (inFlightSliceRecorder) {
    trackFinalSliceSettlement(inFlightSliceRecorder);
  }

  try {
    if (state.mediaRecorder && state.mediaRecorder.state === 'recording') {
      state.mediaRecorder.stop();
    }

    if (state.currentChunkTimeout) {
      clearTimeout(state.currentChunkTimeout);
      state.currentChunkTimeout = null;
    }

    if (state.timerInterval) {
      clearInterval(state.timerInterval);
      state.timerInterval = null;
    }

    releaseCaptureResources(state.captureResources);
    state.captureResources = null;
    state.recordingStream = null;

    const { recordingSessionId, unifiedSessionIdEnabled } =
      useRecordingStore.getState();

    // `null` means this take must use the LEGACY path: either the flag is off
    // or begin-take never stamped a generation. Every unified-only branch
    // below is gated on it, so an unstamped take still uploads its audio.
    const takeGeneration = unifiedTakeGeneration(unifiedSessionIdEnabled);

    // Chunk numbers are assigned at emit time, so this is true iff at least
    // one chunk of THIS take actually emitted (an in-flight slice has no
    // number yet).
    const tookChunkThisTake =
      state.chunkCounter.next > state.takeStartChunkNumber;
    const finalChunkNumber = state.chunkCounter.next - 1;
    const finalChunkId = formatChunkId(finalChunkNumber);

    if (
      tookChunkThisTake &&
      !inFlightSliceRecorder &&
      state.recordedChunks[finalChunkId]
    ) {
      try {
        const client = WhisperClient.getInstance();

        if (takeGeneration !== null) {
          await client.uploadChunk(
            finalChunkNumber,
            state.recordedChunks[finalChunkId].blob,
            true,
            state.takeStartChunkNumber,
            true,
            takeGeneration
          );
        } else {
          await client.uploadChunk(
            finalChunkNumber,
            state.recordedChunks[finalChunkId].blob,
            true,
            state.takeStartChunkNumber
          );
        }

        state.lastChunkIdWithBlob = finalChunkId;
        state.highestChunkNumberWithBlob = finalChunkNumber;
      } catch (error) {
        // The `?final=true` re-upload FAILED, so it delivered no final
        // signal. For a unified session (NEB-100347) the server hard-depends
        // on that signal; fire the recording-complete fallback so the SIGNAL
        // survives even though the blob re-upload failed. The streaming path
        // has usually already uploaded the audio bytes; it is the signal that
        // must not be lost. Flag-off — and an unstamped take, which uploaded
        // via the legacy path and has no unified take server-side — is
        // byte-identical (no fallback).
        if (takeGeneration !== null) {
          if (recordingSessionId) {
            try {
              await sessionsClient.postRecordingComplete(recordingSessionId, {
                finalChunkNumber,
                takeStart: state.takeStartChunkNumber,
                takeGeneration,
              });
            } catch (completeError) {
              logError(
                'audioRecorder',
                'recording-complete fallback failed after final chunk upload error',
                completeError instanceof Error
                  ? completeError.message
                  : String(completeError)
              );
            }
          } else {
            logError(
              'audioRecorder',
              'Cannot send recording-complete fallback: missing recordingSessionId',
              { chunkNumber: finalChunkNumber }
            );
          }
        }

        // The final-chunk re-upload failed, but the recorder has still
        // stopped. Clear the same terminal state the normal completion path
        // clears (below) BEFORE returning: otherwise state.isRecording stays
        // true and a same-session re-record's startRecording early-returns on
        // the isRecording guard, silently no-oping into a stuck-READY recorder
        // (NEB-102803).
        state.isRecording = false;
        state.isPaused = false;
        state.isSwitchingDevice = false;
        state.recordingOptions = null;
        state.activeDeviceId = null;
        useRecordingStore.getState().setIsPaused(false);

        // Surface the failure even when the caller passed no onRecordingError
        // (resetForNewTake calls stopRecording() with no options, making
        // onRecordingError?.() a no-op); without this log the final-chunk
        // upload failure would vanish entirely.
        logError(
          'audioRecorder',
          'Final chunk upload failed on stop',
          error instanceof Error ? error.message : String(error)
        );
        options.onRecordingError?.(
          error instanceof Error ? error : new Error(String(error))
        );
        return;
      }
    } else if (takeGeneration !== null) {
      // No `?final=true` re-upload can fire: a slice is still capturing,
      // and/or this take has no retained final blob. The unified server path
      // hard-depends on a final signal (NEB-100347).
      if (!recordingSessionId) {
        logError(
          'audioRecorder',
          'Cannot send recording-complete: missing recordingSessionId',
          { chunkNumber: finalChunkNumber }
        );
      } else if (inFlightSliceRecorder) {
        // The unsettled slice's outcome is unknown: a dropped slice is
        // never uploaded and consumes no chunk number, and signalling a number
        // the server may never receive would make it expect a chunk that never
        // arrives and force-finalize a false partial. Defer the signal to the
        // slice's terminal points (onstop / upload settle), which know the
        // highest chunk that actually reached the server. Values are captured
        // now — take state may be reset before the terminal fires.
        state.pendingRecordingCompleteSignal = {
          recordingSessionId,
          sliceRecorder: inFlightSliceRecorder,
          takeStart: state.takeStartChunkNumber,
          takeGeneration,
        };
      } else if (tookChunkThisTake) {
        // No slice in flight: finalChunkNumber is the last EMITTED chunk and
        // nothing can upload after it, so send recording-complete immediately.
        try {
          await sessionsClient.postRecordingComplete(recordingSessionId, {
            finalChunkNumber,
            takeStart: state.takeStartChunkNumber,
            takeGeneration,
          });
        } catch (completeError) {
          // The streaming path has usually already uploaded the audio bytes
          // and the server-side sweep is the backstop that recovers the
          // session, so a transient recording-complete reject must not
          // surface a false recording error. Log and proceed so the normal
          // stop-completion path (onRecordingStopped) still runs.
          logError(
            'audioRecorder',
            'recording-complete failed in no-blob stop branch',
            completeError instanceof Error
              ? completeError.message
              : String(completeError)
          );
        }
      }
      // No chunk emitted this take and no slice in flight: nothing reached
      // the server to signal — the server sweep is the backstop.
    }

    // Mark the final chunk regardless of whether a final blob was uploadable.
    // When the trailing slice was dropped the final chunk is never re-uploaded;
    // without this the completion gate would never trip and the flow would
    // depend entirely on the 5s fallback. When a slice was in flight the mark
    // is deferred to its terminal points so it cannot race ahead of that
    // not-yet-expected chunk.
    if (state.mediaRecorder && !inFlightSliceRecorder) {
      state.finalChunkMarked = true;
      checkTranscriptCompletion();
    }

    state.isRecording = false;
    state.isPaused = false;
    state.isSwitchingDevice = false;
    state.recordingOptions = null;
    state.activeDeviceId = null;

    useRecordingStore.getState().setIsPaused(false);

    options.onRecordingStopped?.();
  } catch (error) {
    state.isRecording = false;
    state.isPaused = false;
    state.isSwitchingDevice = false;
    state.recordingOptions = null;
    state.activeDeviceId = null;
    state.mediaRecorder = null;
    releaseCaptureResources(state.captureResources);
    state.captureResources = null;
    state.recordingStream = null;

    useRecordingStore.getState().setIsPaused(false);

    options.onRecordingError?.(
      error instanceof Error ? error : new Error(String(error))
    );
  }
}

export function pauseRecording(): void {
  if (!state.isRecording || state.isPaused) {
    return;
  }

  if (state.continuous) {
    void pauseRecordingContinuous(state.continuous);
    return;
  }

  try {
    if (state.mediaRecorder && state.mediaRecorder.state === 'recording') {
      state.mediaRecorder.stop();
    }

    if (state.currentChunkTimeout) {
      clearTimeout(state.currentChunkTimeout);
      state.currentChunkTimeout = null;
    }

    if (state.timerInterval) {
      clearInterval(state.timerInterval);
      state.timerInterval = null;
    }

    state.isPaused = true;

    useRecordingStore.getState().setIsPaused(true);
  } catch (error) {
    logError('audioRecorder', 'Error pausing recording:', error);
  }
}

/** Returns true only when resume took effect, so RESUMED telemetry excludes pausePending no-ops. */
export function resumeRecording(options: RecordingOptions = {}): boolean {
  if (!state.isRecording || !state.isPaused) {
    return false;
  }

  // Pause acknowledgment is a server-side fence: starting a new take before
  // it arrives could race the checkpoint and let the old take be finalized as
  // live or paused out of order.
  if (state.pausePending) {
    return false;
  }

  // A paused continuous take always has state.continuous === null: every
  // isPaused=true writer nulls it (the pause path before its first await, the
  // resume-failure catch, and the legacy pause branch, which only runs when
  // continuous was already null). So a paused continuous resume flows through
  // the continuous branch below, which starts a fresh run for the resumed take.
  if (useRecordingStore.getState().unifiedSessionIdEnabled) {
    const stream = state.recordingStream;
    if (!stream) {
      logError(
        'audioRecorder',
        'Cannot resume continuous capture: no recording stream'
      );
      return false;
    }
    const resumeTakeStartChunkNumber = state.takeStartChunkNumber;
    state.isPaused = false;
    useRecordingStore.getState().setIsPaused(false);
    const resumeOptions = state.recordingOptions ?? options;
    void startRecordingContinuous(
      resumeOptions,
      stream,
      resumeTakeStartChunkNumber
    ).catch((error) => {
      logError('audioRecorder', 'Error resuming recording:', error);
    });
    return true;
  }

  try {
    state.isPaused = false;

    useRecordingStore.getState().setIsPaused(false);

    // Resume with the options stored at start (NEB-99247): forwarding only the
    // caller's options would lose onRecordingError, so arm-failure escalation
    // and upload failures after a pause would surface nowhere. This also
    // restarts the timer interval that pauseRecording cleared, with the same
    // onTimerUpdate that startRecording installed.
    const resumeOptions = state.recordingOptions ?? options;
    startTimerInterval(resumeOptions);
    recordNextChunk(resumeOptions);
    return true;
  } catch (error) {
    logError('audioRecorder', 'Error resuming recording:', error);
    return false;
  }
}

export function getSessionId(): string | null {
  return state.sessionId;
}

export function getRecordingStream(): MediaStream | null {
  return state.recordingStream;
}

/** Returns the in-flight stop promise when delegation is required so clearSession can await the final tail enqueue; continuous-paused teardown finishes synchronously. */
export function abortRecording(): Promise<void> | void {
  if (!state.isRecording) return;
  if (
    !state.isPaused ||
    !useRecordingStore.getState().unifiedSessionIdEnabled
  ) {
    return stopRecording();
  }

  const pausedRun = state.latestContinuousRun;
  state.isRecording = false;
  state.isPaused = false;
  if (state.timerInterval) {
    clearInterval(state.timerInterval);
    state.timerInterval = null;
  }
  releaseCaptureResourcesForStream(state.recordingStream);
  state.recordingStream = null;
  state.isSwitchingDevice = false;
  state.activeDeviceId = null;
  state.recordingOptions = null;
  state.mediaRecorder = null;
  useRecordingStore.getState().setIsPaused(false);

  if (pausedRun) {
    pausedRun.controlAction = 'pause';
    maybeFinishContinuousRun(pausedRun);
  }
  // Drop the paused-run reference only after maybeFinishContinuousRun (which
  // matches on state.latestContinuousRun): a stale reference would let this
  // aborted run's late uploads leak into the next session's transcript state.
  state.latestContinuousRun = null;
}

export async function clearSession(): Promise<void> {
  // Capture the delegate-to-stop promise (abortRecording returns it) so the
  // upload queue closes only AFTER the stop path enqueues the final tail chunk.
  const stopping = abortRecording();

  state.sessionId = null;
  state.recordingLeaseId = null;
  // Replace the counter OBJECT: a detached run flushing late keeps numbering
  // against the old session's counter (see ChunkCounter).
  state.chunkCounter = { next: 0 };
  const queue = state.uploadQueue;
  state.uploadQueue = null;
  resetTakeState();

  // Await the stop path before resolving, even with no queue to detach:
  // stopRecording clears state.isRecording only after its final-chunk await,
  // so a caller that re-records immediately after clearSession could otherwise
  // hit the isRecording guard in startRecording and silently no-op
  // (NEB-102803).
  await stopping;

  if (queue) {
    // The old session's queue keeps draining detached — its chunks are
    // stamped with their own runs — but nothing new can join it. Detach it
    // only AFTER the stop path has numbered and enqueued the final tail chunk;
    // closing it first drops that chunk.
    queue.close();
  }
}

/**
 * Reset for a re-record of the SAME durable session (unified-session-id mode).
 * Unlike clearSession, preserves state.sessionId (the connection is already
 * registered under the durable id) and the chunk counter — chunk numbering must
 * stay monotonic per session or takes would overwrite each other's S3 keys
 * (sessions/{id}/chunks/NNNN.webm and the GPU transcript outputs).
 */
export async function resetForNewTake(): Promise<void> {
  if (state.isRecording) {
    // Await the stop so state.isRecording is cleared before the caller
    // re-records: a fire-and-forget stopRecording() lets the next
    // startRecording() early-return on the isRecording guard and silently
    // no-op, leaving the recorder stuck READY on a same-session re-record
    // (NEB-102803).
    await stopRecording();
  }

  resetTakeState();
}

export function getTakeStartChunkNumber(): number {
  return state.takeStartChunkNumber;
}

export function setTakeGeneration(takeGeneration: number): void {
  state.takeGeneration = takeGeneration;
}

export function getTakeGeneration(): number | null {
  return state.takeGeneration;
}

/**
 * True when at least one audio chunk was uploaded or enqueued for the current
 * take. When false at stop time no recording-complete was — or will be — sent,
 * so the server never generates a note; the caller must land a terminal
 * no-speech state rather than wait on a generation that will never happen
 * (NEB-100535).
 */
export function hasUploadedAudioChunk(): boolean {
  return (
    state.highestChunkNumberWithBlob !== null ||
    state.latestContinuousRun?.highestUploadedChunkNumber != null ||
    state.latestContinuousRun?.highestEnqueuedChunkNumber != null
  );
}

export function setRecordingLeaseId(leaseId: string | null): void {
  state.recordingLeaseId = leaseId;
}

export function getRecordingLeaseId(): string | null {
  return state.recordingLeaseId;
}

export function setServerChunkCursor(nextChunkNumber: number): void {
  state.serverChunkCursor = nextChunkNumber;
}

function resetTakeState(): void {
  // NEB-100035: the current take starts at the next chunk number to be used.
  // clearSession replaces the counter before calling this (fresh -> 0);
  // resetForNewTake preserves the monotonic counter (re-record -> start of
  // the new take). A continuous start recaptures this after the previous
  // run's tail has settled (race (a)) — this snapshot can be one short.
  state.takeStartChunkNumber = state.chunkCounter.next;
  state.takeGeneration = null;
  state.recordingLeaseId = null;
  state.serverChunkCursor = null;
  state.warnedMissingTakeGeneration = false;
  state.recordedChunks = {};
  state.transcriptLinesByChunk = new Map<string, TranscriptLine[]>();
  state.snapshotWatermark = null;

  state.expectedChunkIds = new Set<string>();
  state.transcriptChunksReceived = new Set<string>();
  state.finalChunkMarked = false;
  state.transcriptCompletionResolve = null;
  state.lastCompletionComplete = false;
  state.lastChunkIdWithBlob = null;
  state.highestChunkNumberWithBlob = null;
  // pendingRecordingCompleteSignal deliberately survives: it belongs to the
  // previous take's unsettled final slice, whose terminal points flush it
  // (matched by recorder identity). Destroying it here would lose that
  // take's final signal — the JSDoc on the field promises survival.
  state.recordingOptions = null;
  state.activeDeviceId = null;
  state.isSwitchingDevice = false;
  state.pausePending = false;
}
